import {
  ContractDiscount,
  ContractDiscountResponse,
  ContractDiscountViewItem,
  ContractPriceComponent,
  ProductDetailsStaticDataDiscountUnit,
  ProductDetailsStaticDataDiscountUnitCode,
} from '@mwe/models';
import { formatAmount, isArrayWithMinOneItem, sumReducer } from '../common/mwe-util';
import dayjs from 'dayjs';
import { formatDate } from '../dates/date.utils';

export const mapContractDiscountResponse = (data: ContractDiscountResponse, whitelist: string[]): ContractDiscount => {
  if (!data) {
    return null;
  }

  const baseDiscountMap = {
    type: data.typ,
    operand: data.operand,
    description: data.bezeichnung,
    validFrom: data.gueltigVon,
    validTo: data.gueltigBis,
  };

  if (whitelist?.includes(data.operand)) {
    return {
      ...baseDiscountMap,
      factor: 0,
    };
  }

  return {
    ...baseDiscountMap,
    factor: Number.parseFloat(data.faktor),
  };
};

export const hasContractActiveDiscount = (discounts: ContractDiscount[]): boolean => {
  return isArrayWithMinOneItem(discounts) && !!discounts.find(d => isContractDiscountActive(d));
};

export const isContractDiscountActive = (discount: ContractDiscount): boolean => {
  if (!discount) {
    return false;
  }

  const validTo = dayjs(discount.validTo);
  const today = dayjs();
  return today.isBefore(validTo, 'day') || today.isSame(validTo, 'day');
};

export const filterActiveContractDiscounts = (discounts: ContractDiscount[]): ContractDiscount[] => {
  if (!isArrayWithMinOneItem(discounts)) {
    return [];
  }

  return discounts.filter(d => isContractDiscountActive(d));
};

export const filterInactiveContractDiscounts = (discounts: ContractDiscount[]): ContractDiscount[] => {
  if (!isArrayWithMinOneItem(discounts)) {
    return [];
  }

  return discounts.filter(d => !isContractDiscountActive(d));
};

export const checkIfAllActiveDiscountsAreWhitelisted = (discounts: ContractDiscount[], whiteList: string[]): boolean => {
  // having no discounts is also valid
  const activeDiscounts = filterActiveContractDiscounts(discounts);
  if (!isArrayWithMinOneItem(activeDiscounts)) {
    return true;
  }

  // whitelist check
  return activeDiscounts.every(d => {
    return !!whiteList?.find(ad => ad.toLowerCase() === d.operand.toLowerCase());
  });
};

// grundpreis has only fet disocunts, no cent
export const getDiscountedGrundPreis = (
  price: number,
  discounts: ContractDiscount[],
  units: ProductDetailsStaticDataDiscountUnit[],
): number => {
  const activeDiscounts = filterActiveContractDiscounts(discounts);
  if (!isArrayWithMinOneItem(activeDiscounts)) {
    return price;
  }
  const days = getSummedFactorFor(activeDiscounts, 'fet', units);
  const priceWithFetDiscount = price * (1 - days / 365);

  const percent = getSummedFactorFor(activeDiscounts, '%', units);
  const percentDiscount = price * percent;

  return priceWithFetDiscount - percentDiscount;
};

export const getRoundedDiscountedGrundPreis = (
  price: number,
  discounts: ContractDiscount[],
  units: ProductDetailsStaticDataDiscountUnit[],
) => Number.parseFloat(getDiscountedGrundPreis(price, discounts, units).toFixed(2));

// verbrauchspreis has fet and cent discounts
export const getDiscountedVerbrauchsPreis = (
  price: ContractPriceComponent,
  discounts: ContractDiscount[],
  units: ProductDetailsStaticDataDiscountUnit[],
  isAddressInMundlCity: boolean,
): number => {
  const activeDiscounts = filterActiveContractDiscounts(discounts);
  if (!isArrayWithMinOneItem(activeDiscounts)) {
    return price?.wert;
  }

  const fetPercentPrice = getDiscountedGrundPreis(price?.wert, discounts, units);

  // cent discount is netto, so add brutto
  let centSumMagic = getSummedFactorFor(activeDiscounts, 'cent', units);

  if (!price?.inkludiertGebrauchsabgabe && price?.inkludiertUmsatzsteuer) {
    centSumMagic = centSumMagic * 1.2;
  }
  // and some extra amount for vienna
  if (isAddressInMundlCity && price?.inkludiertGebrauchsabgabe && price?.inkludiertUmsatzsteuer) {
    centSumMagic = centSumMagic * 1.2 * 1.07;
  }
  return fetPercentPrice - centSumMagic;
};

export const getRoundedDiscountedVerbrauchsPreis = (
  price: ContractPriceComponent,
  discounts: ContractDiscount[],
  units: ProductDetailsStaticDataDiscountUnit[],
  isAddressInMundlCity: boolean,
) => Number.parseFloat(getDiscountedVerbrauchsPreis(price, discounts, units, isAddressInMundlCity).toFixed(2));

function getSummedFactorFor(
  discounts: ContractDiscount[],
  code: ProductDetailsStaticDataDiscountUnitCode,
  units: ProductDetailsStaticDataDiscountUnit[],
): number {
  return discounts
    .filter(d => getDiscountUnitCode(d, units) === code)
    .map(d => d.factor || 0)
    .reduce(sumReducer, 0);
}

export const getDiscountUnitCode = (
  discount: ContractDiscount,
  units: ProductDetailsStaticDataDiscountUnit[],
): ProductDetailsStaticDataDiscountUnitCode => {
  return units?.find(u => discount?.operand.includes(u.key))?.code;
};

export const getContractDiscountViewItemFrom = (
  discount: ContractDiscount,
  unitMapping: ProductDetailsStaticDataDiscountUnit[],
): ContractDiscountViewItem => {
  if (!discount) {
    return null;
  }

  const unit = unitMapping.find(m => discount.operand.startsWith(m.key));

  // 9999-12-31 -> there is no end
  const validTo = discount.validTo === '9999-12-31' ? null : formatDate(discount.validTo);
  return {
    name: discount.description,
    value: getContractDiscountViewItemValue(discount, unit),
    unit: { ...unit },
    validFrom: formatDate(discount.validFrom),
    validTo,
  } as ContractDiscountViewItem;
};

function getContractDiscountViewItemValue(discount: ContractDiscount, unit: ProductDetailsStaticDataDiscountUnit): string {
  switch (unit?.code) {
    case 'cent':
      return formatAmount(discount?.factor);
    case '%':
      return formatAmount(discount?.factor * 100);
    default:
      return discount?.factor + '';
  }
}
